import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Box, IconButton, Popover, Tooltip, Typography,
  RadioGroup, FormControlLabel, Radio, TextField, Stack, MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import GridViewIcon from "@mui/icons-material/GridView";
import CloseIcon from "@mui/icons-material/Close";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import CloseOutlined from "@ant-design/icons/CloseOutlined";
import CameraCell from "./CameraCell";
import DraggableExitFullscreenButton from "./DraggableExitFullscreenButton";
import ModalContainer from "components/ModalContainer";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";
import { LAYOUT_TYPES } from "../../const";
import { FormattedMessage, useIntl } from "react-intl";

const DEFAULT_LAYOUT = "4x4"; // default 16 slots

/** Mini preview icon for each layout option in the Window Division panel */
const LayoutPreviewIcon = ({ layoutKey, cols, rows, total }) => {
  const SZ = 30;
  const border = "1.5px solid currentColor";
  const inner = "1px solid currentColor";

  // 6grids: 3×3 grid — top-left big (2×2), right column 3 cells, bottom row 3 cells
  // ┌────────┬──┐
  // │        │R1│
  // │  big   ├──┤
  // │        │R2│
  // ├──┬─────┼──┤
  // │B1│ B2  │R3│
  // └──┴─────┴──┘
  if (layoutKey === "6grids") {
    return (
      <Box
        sx={{
          width: SZ,
          height: SZ,
          border,
          display: "flex",
          boxSizing: "border-box",
        }}
      >
        {/* Left 2/3: big cell + 2 bottom cells */}
        <Box sx={{ flex: "0 0 66.67%", display: "flex", flexDirection: "column", borderRight: inner }}>
          <Box sx={{ flex: "0 0 66.67%", borderBottom: inner }} />
          <Box sx={{ flex: 1, display: "flex" }}>
            <Box sx={{ flex: 1, borderRight: inner }} />
            <Box sx={{ flex: 1 }} />
          </Box>
        </Box>
        {/* Right 1/3: 3 stacked cells */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Box sx={{ flex: 1, borderBottom: inner }} />
          <Box sx={{ flex: 1, borderBottom: inner }} />
          <Box sx={{ flex: 1 }} />
        </Box>
      </Box>
    );
  }

  // 8grids: top-left big (3/4 w × 3/4 h), right column 3 cells, bottom row 4 cells
  // ┌─────────┬──┐
  // │         │R1│
  // │   big   ├──┤
  // │         │R2│
  // │         ├──┤
  // │         │R3│
  // ├──┬──┬───┴──┤
  // │B1│B2│ B3│B4│
  // └──┴──┴───┴──┘
  if (layoutKey === "8grids") {
    return (
      <Box
        sx={{
          width: SZ,
          height: SZ,
          border,
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
        }}
      >
        {/* Top section: big (3/4 w) + 3 stacked right cells (1/4 w) */}
        <Box sx={{ flex: "0 0 75%", display: "flex", borderBottom: inner }}>
          <Box sx={{ flex: "0 0 75%", borderRight: inner }} />
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <Box sx={{ flex: 1, borderBottom: inner }} />
            <Box sx={{ flex: 1, borderBottom: inner }} />
            <Box sx={{ flex: 1 }} />
          </Box>
        </Box>
        {/* Bottom row: 4 equal cells */}
        <Box sx={{ flex: 1, display: "flex" }}>
          <Box sx={{ flex: 1, borderRight: inner }} />
          <Box sx={{ flex: 1, borderRight: inner }} />
          <Box sx={{ flex: 1, borderRight: inner }} />
          <Box sx={{ flex: 1 }} />
        </Box>
      </Box>
    );
  }

  // 10grids: top row 2 large cells (1/2 w × ~50% h), then 2 rows of 4 equal cells
  // ┌──────┬──────┐
  // │  T1  │  T2  │
  // │      │      │
  // ├──┬───┴──┬───┤
  // │M1│ M2│M3│M4 │
  // ├──┼───┼──┼───┤
  // │B1│ B2│B3│B4 │
  // └──┴───┴──┴───┘
  if (layoutKey === "10grids") {
    return (
      <Box
        sx={{
          width: SZ,
          height: SZ,
          border,
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
        }}
      >
        {/* Top: 2 large equal cells */}
        <Box sx={{ flex: "0 0 50%", display: "flex", borderBottom: inner }}>
          <Box sx={{ flex: 1, borderRight: inner }} />
          <Box sx={{ flex: 1 }} />
        </Box>
        {/* Middle: 4 equal cells */}
        <Box sx={{ flex: 1, display: "flex", borderBottom: inner }}>
          <Box sx={{ flex: 1, borderRight: inner }} />
          <Box sx={{ flex: 1, borderRight: inner }} />
          <Box sx={{ flex: 1, borderRight: inner }} />
          <Box sx={{ flex: 1 }} />
        </Box>
        {/* Bottom: 4 equal cells */}
        <Box sx={{ flex: 1, display: "flex" }}>
          <Box sx={{ flex: 1, borderRight: inner }} />
          <Box sx={{ flex: 1, borderRight: inner }} />
          <Box sx={{ flex: 1, borderRight: inner }} />
          <Box sx={{ flex: 1 }} />
        </Box>
      </Box>
    );
  }

  if (layoutKey === "4x4") {
    return (
      <Box
        sx={{
          width: SZ,
          height: SZ,
          border: "1.5px solid currentColor",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11,
          fontWeight: 700,
          boxSizing: "border-box",
        }}
      >
        {total}
      </Box>
    );
  }

  if (cols && rows) {
    return (
      <Box
        sx={{
          width: SZ,
          height: SZ,
          border: "1.5px solid currentColor",
          display: "grid",
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gridTemplateRows: `repeat(${rows}, 1fr)`,
          boxSizing: "border-box",
        }}
      >
        {Array.from({ length: total }).map((_, i) => (
          <Box
            key={i}
            sx={{
              borderRight: i % cols < cols - 1 ? "1px solid currentColor" : "none",
              borderBottom: Math.floor(i / cols) < rows - 1 ? "1px solid currentColor" : "none",
            }}
          />
        ))}
      </Box>
    );
  }
  return (
    <Box
      sx={{
        width: SZ,
        height: SZ,
        border: "1.5px solid currentColor",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 11,
        fontWeight: 700,
        boxSizing: "border-box",
      }}
    >
      {total}
    </Box>
  );
};

/**
 * Special grid layouts that cannot be expressed as a simple repeat(cols, 1fr).
 * templateAreas : CSS grid-template-areas string
 * slotAreas     : ordered list of area names mapping slot index → gridArea
 * templateCols  : gridTemplateColumns value
 */
const SPECIAL_GRID_LAYOUTS = {
  "6grids": {
    templateCols: "repeat(3, 1fr)",
    templateAreas: '"big big r1" "big big r2" "b1 b2 r3"',
    // slot 0→big, 1→r1, 2→r2, 3→b1, 4→b2, 5→r3
    slotAreas: ["big", "r1", "r2", "b1", "b2", "r3"],
  },
  "8grids": {
    templateCols: "repeat(4, 1fr)",
    templateAreas: '"big big big r1" "big big big r2" "big big big r3" "b1 b2 b3 b4"',
    // slot 0→big, 1→r1, 2→r2, 3→r3, 4→b1, 5→b2, 6→b3, 7→b4
    slotAreas: ["big", "r1", "r2", "r3", "b1", "b2", "b3", "b4"],
  },
  "10grids": {
    templateCols: "repeat(4, 1fr)",
    templateAreas: '"t1 t1 t2 t2" "t1 t1 t2 t2" "m1 m2 m3 m4" "b1 b2 b3 b4"',
    // slot 0→t1, 1→t2, 2→m1, 3→m2, 4→m3, 5→m4, 6→b1, 7→b2, 8→b3, 9→b4
    // all cells get aspectRatio; t1/t2 span 2 cols so height = 2× small cells
    slotAreas: ["t1", "t2", "m1", "m2", "m3", "m4", "b1", "b2", "b3", "b4"],
  },
  "13grids": {
    templateCols: "repeat(4, 1fr)",
    templateAreas: '"big big r1 r2" "big big r3 r4" "m1 m2 m3 m4" "b1 b2 b3 b4"',
    // slot 0→big, 1→r1, 2→r2, 3→r3, 4→r4, 5→m1..8→m4, 9→b1..12→b4
    // big spans 2×2 cols, all cells get aspectRatio for consistent row heights
    slotAreas: ["big", "r1", "r2", "r3", "r4", "m1", "m2", "m3", "m4", "b1", "b2", "b3", "b4"],
  },
  "14grids": {
    templateCols: "repeat(4, 1fr)",
    templateAreas: '"t1 t1 t2 t2" "m1 m2 m3 m4" "r1 r2 r3 r4" "b1 b2 b3 b4"',
    // slot 0→t1, 1→t2, 2→m1..5→m4, 6→r1..9→r4, 10→b1..13→b4
    // t1/t2 span 2 cols (1/2 width each), all cells get aspectRatio
    slotAreas: ["t1", "t2", "m1", "m2", "m3", "m4", "r1", "r2", "r3", "r4", "b1", "b2", "b3", "b4"],
  },
};

/**
 * VideoWall
 * Displays the camera grid for the currently active view.
 *
 * Props:
 *   activeView      - the view object currently being displayed
 *                     { id, name, layout, cameras[] }
 *   views           - saved views list [{ id, name }], used to populate the
 *                     "Existing View" picker in the Save View modal
 *   viewDetailsMap  - recorded detail per view id { [id]: { version, name, ... } },
 *                     used to carry over `version` when updating an existing view
 *   onCreateView    - callback(newViewData) called when user confirms Save View → New View
 *   onUpdateView    - callback(viewId, updatedData) called when user confirms
 *                     Save View → Existing View
 *   slots           - array of { slotIndex, deviceId } for currently assigned cameras
 */
const VideoWall = ({
  activeView,
  views = [],
  viewDetailsMap = {},
  onCreateView,
  onUpdateView,
  onAssignCamera,
  onMoveSlot,
  onRemoveCamera,
  onRequestStartViewing,
  onLayoutChange,
  onCloseView,
  slots = [],
}) => {
  const theme = useTheme();
  const intl = useIntl();
  const [layout, setLayout] = useState(activeView?.layout ?? DEFAULT_LAYOUT);

  // Keep local layout in sync when user switches saved views
  useEffect(() => {
    if (activeView?.layout) setLayout(activeView.layout);
  }, [activeView?.layout, activeView?.id]);
  // Index slots by slotIndex so each grid cell can look up its assigned
  // camera { deviceId, name } — populated when the user presses ▶ (handlePlayView).
  const slotsByIndex = useMemo(() => {
    const map = {};
    slots.forEach((slot) => {
      if (slot && slot.slotIndex !== undefined && slot.slotIndex !== null) {
        map[slot.slotIndex] = slot;
      }
    });
    return map;
  }, [slots]);
  const [layoutAnchor, setLayoutAnchor] = useState(null);
  const [immersive, setImmersive] = useState(false);
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const [saveAs, setSaveAs] = useState("new");
  const [viewName, setViewName] = useState("View 1");
  // id of the saved view selected in the "Existing View" picker
  const [existingViewId, setExistingViewId] = useState("");
  const [savingView, setSavingView] = useState(false);
  const [saveViewError, setSaveViewError] = useState("");

  const handleLayoutOpen = (e) => setLayoutAnchor(e.currentTarget);
  const handleLayoutClose = () => setLayoutAnchor(null);
  const enterImmersive = useCallback(() => setImmersive(true), []);
  const exitImmersive = useCallback(() => setImmersive(false), []);

  useEffect(() => {
    if (!immersive) return undefined;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (e) => {
      if (e.key === "Escape") exitImmersive();
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [immersive, exitImmersive]);
  const handleSelectLayout = (key) => {
    setLayout(key);
    onLayoutChange?.(key);
    handleLayoutClose();
  };

  const handleSaveViewOpen = () => {
    // default to the view currently on screen, falling back to the first saved view
    setExistingViewId(
      activeView?.id && views.some((v) => v.id === activeView.id)
        ? activeView.id
        : views[0]?.id ?? ""
    );
    setSaveViewError("");
    setSaveViewOpen(true);
  };
  const handleSaveViewClose = () => {
    if (savingView) return;
    setSaveViewOpen(false);
  };

  // Keep the modal open until the server confirms, so a rejected save is visible.
  const submitSave = async (request) => {
    setSavingView(true);
    setSaveViewError("");
    const result = await request;
    setSavingView(false);
    if (result && result.ok === false) {
      setSaveViewError(result.message || "Unable to save the view.");
      return;
    }
    setSaveViewOpen(false);
  };

  // Submit the current layout (gridTemplate) and camera arrangement (ordered slots
  // with deviceId) to either create a new view or overwrite the selected existing one.
  const handleSaveViewSubmit = () => {
    if (savingView) return;
    const activeLayoutKey = activeView?.layout ?? layout;
    const gridTemplate = LAYOUT_TYPES.findIndex((l) => l.key === activeLayoutKey) + 1;
    const orderedSlots = [...slots]
      .sort((a, b) => a.slotIndex - b.slotIndex)
      .map(({ slotIndex, deviceId }) => ({ slotIndex, deviceId }));

    if (saveAs === "existing") {
      if (!existingViewId) return;
      const targetView = views.find((v) => v.id === existingViewId);
      const targetDetail = viewDetailsMap[existingViewId];

      submitSave(onUpdateView?.(existingViewId, {
        version: targetDetail?.version ?? 1,
        name: targetDetail?.name ?? targetView?.name ?? "",
        gridTemplate,
        slots: orderedSlots,
      }));
      return;
    }

    if (!viewName.trim()) return;
    submitSave(onCreateView?.({
      name: viewName.trim(),
      gridTemplate,
      slots: orderedSlots,
    }));
  };

  const currentLayout =
    LAYOUT_TYPES.find((l) => l.key === (activeView?.layout ?? layout)) ??
    LAYOUT_TYPES.find((l) => l.key === DEFAULT_LAYOUT);

  const totalSlots = currentLayout.total;
  const cols = currentLayout.cols ?? Math.ceil(Math.sqrt(totalSlots));
  const rows = currentLayout.rows ?? Math.ceil(totalSlots / cols);
  const gridPadding = immersive ? "4px" : "12px";
  const cellFillSx = immersive
    ? { minHeight: 0, height: "100%", aspectRatio: "unset" }
    : { aspectRatio: "225.81 / 152.68" };
  const gridContainerSx = {
    width: "100%",
    display: "grid",
    p: gridPadding,
    boxSizing: "border-box",
    ...(immersive && {
      flex: 1,
      minHeight: 0,
      height: "100%",
    }),
  };

  return (
    <>
    <Box
      sx={{
        backgroundColor: theme.palette.primary.white,
        borderRadius: immersive
          ? 0
          : `0 ${theme.shape.customBorderRadius[4]} ${theme.shape.customBorderRadius[4]} 0`,
        flex: 1,
        display: "flex",
        flexDirection: "column",
        boxShadow: immersive ? "none" : "1px 1px 4px 0 rgba(0,0,0,0.2)",
        overflowY: immersive ? "hidden" : "auto",
        overflowX: "hidden",
        ...(immersive && {
          position: "fixed",
          inset: 0,
          zIndex: 1300,
          height: "100vh",
        }),
      }}
    >
      {immersive && (
        <DraggableExitFullscreenButton onExit={exitImmersive} />
      )}

      {/* ── Toolbar ── */}
      {!immersive && (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mx: "12px",
          mt: "12px",
          mb: "8px",
          px: 2,
          py: "6px",
          border: "1px solid rgba(0,0,0,0.1)",
          borderRadius: theme.shape.customBorderRadius[1],
          flexShrink: 0,
        }}
      >
        {/* Left: hint text */}
        <Typography
          variant="body2"
          sx={{ color: theme.palette.text.secondary, fontSize: 13 }}
        >
          ✦&nbsp;
          <FormattedMessage
            id="liveview.videowall.hint"
            defaultMessage="Select cameras on the left to play. Drag to rearrange the live view grid."
          />
        </Typography>

        {/* Right: action icons */}
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {/* Layout selector — shows current slot count */}
          <Tooltip title={<FormattedMessage id="liveview.videowall.layout" defaultMessage="Layout" />}>
            <IconButton
              size="small"
              onClick={handleLayoutOpen}
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: layoutAnchor ? theme.palette.primary.main : theme.palette.text.secondary,
                gap: "2px",
              }}
            >
              <GridViewIcon fontSize="small" />
              <span style={{ fontSize: 11, fontWeight: 700, lineHeight: 1 }}>
                {totalSlots}
              </span>
            </IconButton>
          </Tooltip>

          {/* Close */}
          <Tooltip title={<FormattedMessage id="close" defaultMessage="Close" />}>
            <IconButton
              size="small"
              sx={{ color: theme.palette.text.secondary }}
              onClick={() => onCloseView?.()}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Save View */}
          <Tooltip title={<FormattedMessage id="liveview.videowall.saveView" defaultMessage="Save View" />}>
            <IconButton size="small" sx={{ color: theme.palette.text.secondary }} onClick={handleSaveViewOpen}>
              <SaveOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Fullscreen */}
          <Tooltip title={<FormattedMessage id="liveview.videowall.fullscreen" defaultMessage="Fullscreen" />}>
            <IconButton size="small" sx={{ color: theme.palette.text.secondary }} onClick={enterImmersive}>
              <FullscreenIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      )}

      {/* ── Window Division Popover ── */}
      <Popover
        open={Boolean(layoutAnchor)}
        anchorEl={layoutAnchor}
        onClose={handleLayoutClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          elevation: 4,
          sx: {
            mt: "8px",
            p: "16px",
            borderRadius: "10px",
            overflow: "visible",
            minWidth: 220,
            "&::before": {
              content: '""',
              position: "absolute",
              top: -7,
              left: "50%",
              transform: "translateX(-50%) rotate(45deg)",
              width: 14,
              height: 14,
              bgcolor: "background.paper",
              boxShadow: "-2px -2px 4px rgba(0,0,0,0.08)",
            },
          },
        }}
      >
        <Typography
          sx={{ fontSize: 13, fontWeight: 600, mb: "12px", color: theme.palette.text.primary }}
        >
          <FormattedMessage id="liveview.videowall.windowDivision" defaultMessage="Window Division" />
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px" }}>
          {LAYOUT_TYPES.map((lt) => {
            const isSelected = lt.key === (activeView?.layout ?? layout);
            return (
              <IconButton
                key={lt.key}
                size="small"
                onClick={() => handleSelectLayout(lt.key)}
                sx={{
                  borderRadius: "6px",
                  border: `1.5px solid ${isSelected ? theme.palette.primary.main : "rgba(0,0,0,0.15)"}`,
                  color: isSelected ? theme.palette.primary.main : theme.palette.text.secondary,
                  backgroundColor: isSelected ? `${theme.palette.primary.main}14` : "transparent",
                  p: "6px",
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    backgroundColor: `${theme.palette.primary.main}0a`,
                  },
                }}
              >
                <LayoutPreviewIcon layoutKey={lt.key} cols={lt.cols} rows={lt.rows} total={lt.total} />
              </IconButton>
            );
          })}
        </Box>
      </Popover>

      {/* ── Camera Grid ── */}
      {(() => {
        const special = SPECIAL_GRID_LAYOUTS[currentLayout.key];

        if (special) {
          return (
            <Box
              sx={{
                ...gridContainerSx,
                gridTemplateColumns: special.templateCols,
                gridTemplateAreas: special.templateAreas,
              }}
            >
              {special.slotAreas.map((area, idx) => (
                <CameraCell
                  key={`${activeView?.id ?? "wall"}-${idx}`}
                  slotIndex={idx}
                  slot={slotsByIndex[idx]}
                  onAssignCamera={onAssignCamera}
                  onMoveSlot={onMoveSlot}
                  onRemoveCamera={onRemoveCamera}
                  onRequestStartViewing={onRequestStartViewing}
                  layout={currentLayout.key}
                  extraSx={{
                    gridArea: area,
                    ...(!(special.bigAreas ?? ["big"]).includes(area) && cellFillSx),
                  }}
                />
              ))}
            </Box>
          );
        }

        return (
          <Box
            sx={{
              ...gridContainerSx,
              gridTemplateColumns: `repeat(${cols}, 1fr)`,
              ...(immersive && { gridTemplateRows: `repeat(${rows}, 1fr)` }),
            }}
          >
            {Array.from({ length: totalSlots }).map((_, idx) => (
              <CameraCell
                key={`${activeView?.id ?? "wall"}-${idx}`}
                slotIndex={idx}
                slot={slotsByIndex[idx]}
                onAssignCamera={onAssignCamera}
                onMoveSlot={onMoveSlot}
                onRemoveCamera={onRemoveCamera}
                onRequestStartViewing={onRequestStartViewing}
                layout={currentLayout.key}
                extraSx={cellFillSx}
              />
            ))}
          </Box>
        );
      })()}
    </Box>

      {/* ── Save View Modal ── */}
      <ModalContainer display={saveViewOpen} modalWidth="500px">
        <Stack spacing={3}>
          {/* Header */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontSize: "18px", fontWeight: "bold", color: "#172664" }}>
              <FormattedMessage id="liveview.saveView.title" defaultMessage="Save View" />
            </Typography>
            <IconButton onClick={handleSaveViewClose} sx={{ p: 0.5 }}>
              <CloseOutlined style={{ fontSize: "18px", color: "rgba(0,0,0,0.45)" }} />
            </IconButton>
          </Stack>

          {/* Save as */}
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography sx={{ width: 100, fontSize: "14px", color: "rgba(0,0,0,0.65)", flexShrink: 0 }}>
              <FormattedMessage id="liveview.saveView.saveAs" defaultMessage="Save as" />
            </Typography>
            <RadioGroup row value={saveAs} onChange={(e) => setSaveAs(e.target.value)}>
              <FormControlLabel
                value="existing"
                control={<Radio size="small" />}
                label={<FormattedMessage id="liveview.saveView.existingView" defaultMessage="Existing View" />}
              />
              <FormControlLabel
                value="new"
                control={<Radio size="small" />}
                label={<FormattedMessage id="liveview.saveView.newView" defaultMessage="New View" />}
              />
            </RadioGroup>
          </Stack>

          {/* View Name (New View) / Existing View picker */}
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography sx={{ width: 100, fontSize: "14px", color: "rgba(0,0,0,0.65)", flexShrink: 0 }}>
              {saveAs === "existing" ? (
                <FormattedMessage id="liveview.saveView.existingView" defaultMessage="Existing View" />
              ) : (
                <FormattedMessage id="liveview.saveView.viewName" defaultMessage="View Name" />
              )}
            </Typography>
            {saveAs === "existing" ? (
              <TextField
                select
                fullWidth
                size="small"
                value={existingViewId}
                onChange={(e) => setExistingViewId(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontSize: "14px",
                  },
                }}
              >
                {views.map((v) => (
                  <MenuItem key={v.id} value={v.id}>
                    {v.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                fullWidth
                size="small"
                value={viewName}
                onChange={(e) => setViewName(e.target.value)}
                inputProps={{ maxLength: 50 }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontSize: "14px",
                  },
                }}
              />
            )}
          </Stack>

          {saveViewError && (
            <Typography role="alert" sx={{ fontSize: "13px", color: theme.palette.error.main }}>
              {saveViewError}
            </Typography>
          )}

          {/* Divider */}
          <Box sx={{ borderTop: "1px solid rgba(0,0,0,0.1)" }} />

          {/* Footer Buttons */}
          <Stack direction="row" justifyContent="center" spacing={2}>
            <OutlineButton
              width="100px"
              height="32px"
              onClick={handleSaveViewClose}
              disabled={savingView}
            >
              <FormattedMessage id="cancel" defaultMessage="Cancel" />
            </OutlineButton>
            <SolidButton
              width="100px"
              height="32px"
              onClick={handleSaveViewSubmit}
              disabled={savingView}
            >
              {saveAs === "existing" ? (
                <FormattedMessage id="save" defaultMessage="Save" />
              ) : (
                <FormattedMessage id="create" defaultMessage="Create" />
              )}
            </SolidButton>
          </Stack>
        </Stack>
      </ModalContainer>
    </>
  );
};

export default VideoWall;
